import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Phone, CalendarCheck, Users, ShieldCheck, Sun } from "lucide-react";
import { BRAND, waLink } from "@/data/site";
import { WaIcon, ease, useLang, Segs } from "./common";

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span className="block" initial={{ y: "110%", rotate: 4 }} animate={{ y: 0, rotate: 0 }} transition={{ duration: 1.2, delay: 0.2 + i * 0.13, ease }}>
      {children}
    </motion.span>
  </span>
);

const ICONS = [CalendarCheck, Users, ShieldCheck];

const Fade = ({ d, children, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: d, ease }}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const { t } = useLang();
  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36 pb-16 lg:pb-24">
      <div className="absolute inset-0 panel-lines opacity-60 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <Fade d={0.05} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#0F295B]/10 pl-1.5 pr-4 py-1.5 shadow-sm mb-8">
            <span className="w-7 h-7 rounded-full bg-[#F39C12] grid place-items-center"><Sun className="w-4 h-4 text-white" /></span>
            <span className="font-mono2 text-[11px] sm:text-xs tracking-[0.18em] uppercase text-[#0F295B]">Clean Energy | Brighter Tomorrow</span>
          </Fade>
          <h1 data-testid="hero-headline" className="font-display font-bold text-[#0F295B] text-[2.5rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[5.4rem] tracking-[-0.03em]">
            <Line i={0}>{t.hero.lines[0]}</Line>
            <Line i={1}>{t.hero.lines[1]}</Line>
            <Line i={2}><Segs segs={t.hero.lines.slice(2)} /></Line>
          </h1>
          <Fade d={0.75}>
            <p data-testid="hero-subheadline" className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A5B78]">
              {t.hero.sub} <strong className="text-[#0F295B]">{t.hero.city}</strong>.
            </p>
          </Fade>
          <Fade d={0.9} className="mt-12 grid sm:grid-cols-3 gap-3 max-w-2xl">
            {t.hero.badges.map((b, i) => {
              const I = ICONS[i];
              return (
                <div key={b} data-testid={`trust-badge-${i}`} className="flex items-center gap-3 rounded-2xl bg-white/80 backdrop-blur border border-[#0F295B]/10 px-4 py-3">
                  <I className="w-5 h-5 shrink-0 text-[#00A651]" />
                  <span className="text-sm font-semibold text-[#0F295B] leading-tight">{b}</span>
                </div>
              );
            })}
          </Fade>
        </div>
        <HeroVisual />
      </div>
    </section>
  );
};

const HeroVisual = () => {
  const { t } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 110, damping: 16 });
  const { scrollY } = useScroll();
  const sunY = useTransform(scrollY, [0, 900], [0, 220]);
  const imgScale = useTransform(scrollY, [0, 900], [1.05, 1.25]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="lg:col-span-5 relative h-[460px] sm:h-[580px] lg:h-[640px]" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-visual">
      <motion.div style={{ y: sunY }} className="absolute -top-4 right-0 sm:right-6 w-44 h-44 sm:w-60 sm:h-60 z-0">
        <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full spin-slow" aria-hidden="true">
          {Array.from({ length: 24 }).map((_, i) => (
            <rect key={i} x="98" y="0" width="4" height={i % 2 ? 22 : 34} rx="2" fill="#F39C12" transform={`rotate(${i * 15} 100 100)`} />
          ))}
        </svg>
        <div className="absolute inset-[22%] rounded-full bg-[radial-gradient(circle_at_35%_35%,#FFD27A,#F39C12_60%)] shadow-[0_0_80px_20px_rgba(243,156,18,0.35)]" />
      </motion.div>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-x-0 bottom-0 top-20 sm:top-24 z-10">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }}
          transition={{ duration: 1.4, delay: 0.35, ease }}
          className="absolute inset-0 overflow-hidden arch shadow-[0_40px_80px_-30px_rgba(15,41,91,0.6)] bg-[#0F295B]"
        >
          <motion.img style={{ scale: imgScale }} src="/assets/hero.jpg" alt={t.hero.imgAlt} className="w-full h-full object-cover object-[60%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F295B]/55 via-transparent to-transparent" />
        </motion.div>
        <motion.div style={{ translateZ: 70 }} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3, duration: 0.8, ease }}
          className="absolute left-0 sm:-left-8 bottom-24 rounded-2xl bg-white/90 backdrop-blur-xl border border-white px-4 py-3 shadow-xl" data-testid="hero-chip-install">
          <p className="font-mono2 text-[11px] tracking-widest text-[#00A651] uppercase">{t.hero.installFrom}</p>
          <p className="font-display text-xl text-[#0F295B]">₹1.75<span className="text-sm text-[#4A5B78]"> / PW</span></p>
        </motion.div>
        <motion.div style={{ translateZ: 110 }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5, duration: 0.8, ease }}
          className="absolute right-2 sm:-right-4 bottom-6 rounded-2xl bg-[#0F295B] text-white px-4 py-3 shadow-xl" data-testid="hero-chip-amc">
          <p className="font-mono2 text-[11px] tracking-widest text-[#F39C12] uppercase">{t.hero.amcFrom}</p>
          <p className="font-display text-xl">₹399<span className="text-sm text-slate-300"> {t.hero.perMonth}</span></p>
        </motion.div>
      </motion.div>
    </div>
  );
};
